// src/pages/AcceptableUse.js
// Acceptable Use Policy Page

import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../components/common';
import './Upgrade.css';

function AcceptableUse() {

  return (
    <div className="upgrade-container">
      <SEOHead
        title="Acceptable Use Policy | DriftAlarm"
        description="DriftAlarm Acceptable Use Policy covering authorized scanning, prohibited activities, rate limits, and responsible disclosure for our EASM platform."
        path="/acceptable-use"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Acceptable Use Policy — DriftAlarm",
            "url": "https://www.driftalarm.com/acceptable-use",
            "dateModified": "2026-03-13T00:00:00-05:00",
            "publisher": {
              "@type": "Organization",
              "name": "DriftAlarm",
              "url": "https://www.driftalarm.com",
              "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" },
              "foundingDate": "2026",
              "sameAs": ["https://www.driftalarm.com"]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
              { "@type": "ListItem", "position": 2, "name": "Acceptable Use Policy" }
            ]
          }
        ]}
      />
      <PublicHeader activeLink={null} />

      {/* Main Content */}
      <div className="upgrade-content legal-content">
        <h1>Acceptable Use Policy</h1>
        <p className="legal-updated">Last Updated: March 2026</p>

        <section className="legal-section">
          <h2>1. Purpose</h2>
          <p>
            This Acceptable Use Policy ("AUP") defines the acceptable and prohibited uses of the DriftAlarm platform.
          </p>
          <p>
            This AUP is incorporated by reference into the <Link to="/terms">Terms of Service</Link>.
          </p>
          <p>
            Violation of this AUP may result in suspension or termination of your account.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Authorized Use</h2>
          <p>
            DriftAlarm is designed for External Attack Surface Management of YOUR assets.
          </p>
          <p>
            You may only scan domains, IP addresses, and IP ranges that meet ONE of the following criteria:
          </p>
          <ul>
            <li>You own the asset</li>
            <li>You have explicit written authorization from the asset owner to perform security scanning</li>
            <li>You are acting as an authorized agent (e.g., MSP, security consultant) with documented client authorization</li>
          </ul>
          <p>
            You must maintain proof of authorization for all scanned assets and make it available to DriftAlarm upon request.
          </p>
          <p>
            Authorization must cover the types of scanning performed by DriftAlarm, including: DNS enumeration, port scanning,
            web crawling, vulnerability detection, SSL/TLS testing, technology fingerprinting, and DAST analysis.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Prohibited Activities</h2>
          <p>You may NOT use DriftAlarm to:</p>
          <ul>
            <li>Scan targets without proper authorization</li>
            <li>Conduct denial-of-service (DoS) or distributed denial-of-service (DDoS) attacks</li>
            <li>Attempt to exploit vulnerabilities discovered through the Service</li>
            <li>Use the VALIDATE feature against targets you do not own or are not authorized to test</li>
            <li>Resell, redistribute, or commercially repackage scan results without written permission</li>
            <li>Circumvent tier limits, rate limits, or concurrency restrictions</li>
            <li>Use API keys for automated mass scanning beyond your tier limits</li>
            <li>Share account credentials with unauthorized parties</li>
            <li>Use the Service to develop competing products</li>
            <li>Interfere with or disrupt the Service or other users' access</li>
            <li>Upload malicious content or attempt to compromise the platform</li>
            <li>Use scan results to blackmail, extort, or coerce asset owners</li>
            <li>Misrepresent your identity or authorization status</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Rate Limits and Fair Use</h2>
          <p>Scanning is subject to the following tier-specific limits:</p>
          <ul>
            <li><strong>Trial:</strong> 10 Fast/wk, 5 Deep/wk, 3 Scheduled/wk, No API</li>
            <li><strong>Standard:</strong> 10 Fast/wk, 5 Deep/wk, 3 Scheduled/wk, 60 API req/min</li>
            <li><strong>Pro:</strong> 50 Fast/wk, 25 Deep/wk, 15 Scheduled/wk, 300 API req/min</li>
            <li><strong>Enterprise:</strong> Unlimited scans, 600 API req/min</li>
          </ul>
          <p>
            System-wide maximum concurrent scans: 10.
          </p>
          <p>
            Excessive scanning that impacts platform performance may be throttled.
          </p>
          <p>Custom rule limits:</p>
          <ul>
            <li><strong>Trial:</strong> 5</li>
            <li><strong>Standard:</strong> 10</li>
            <li><strong>Pro:</strong> 25</li>
            <li><strong>Enterprise:</strong> Unlimited</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Responsible Disclosure</h2>
          <p>
            If DriftAlarm scanning reveals vulnerabilities in systems you are authorized to scan, you are
            encouraged to follow responsible disclosure practices.
          </p>
          <p>
            Do not publicly disclose vulnerability details before the asset owner has been notified and had
            reasonable time to remediate.
          </p>
          <p>
            DriftAlarm is not liable for vulnerabilities discovered during authorized scans.
          </p>
          <p>
            If you discover a vulnerability in DriftAlarm itself, please report it to{' '}
            <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. AI Analysis Guidelines</h2>
          <p>
            AI-generated remediation guidance, risk scoring, and reports are advisory only.
          </p>
          <p>
            AI recommendations should be verified by qualified personnel before implementation.
          </p>
          <p>
            AI analysis accuracy depends on the quality and completeness of scan data.
          </p>
          <p>
            Do not rely solely on AI recommendations for critical security decisions.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Data Usage</h2>
          <p>
            You may use scan data and reports for internal security purposes.
          </p>
          <p>
            Sharing scan results with authorized personnel within your organization is permitted.
          </p>
          <p>
            Sharing scan results with third parties (clients, auditors, regulators) is permitted when the data
            pertains to assets you own or are authorized to manage.
          </p>
          <p>
            Using scan data for competitive intelligence against the scanned organization is prohibited.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Enforcement</h2>
          <p>DriftAlarm reserves the right to:</p>
          <ul>
            <li>Investigate suspected AUP violations</li>
            <li>Suspend scanning for specific targets pending investigation</li>
            <li>Temporarily or permanently suspend accounts for confirmed violations</li>
            <li>Report illegal activities to appropriate law enforcement</li>
          </ul>
          <p>
            For non-egregious violations: we will provide email notice and a reasonable period to cure before suspension.
          </p>
          <p>
            For egregious violations (unauthorized scanning, attempted exploitation, DoS attacks): immediate
            suspension without prior notice.
          </p>
          <p>
            Appeals may be directed to{' '}
            <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Reporting Violations</h2>
          <p>
            To report a suspected AUP violation:{' '}
            <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>
          </p>
          <p>
            To report unauthorized scanning of your assets:{' '}
            <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>
          </p>
          <p>
            Include: description of the issue, relevant IP addresses/domains, timestamps, and any supporting evidence.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Related Policies</h2>
          <ul>
            <li><Link to="/terms">Terms of Service</Link></li>
            <li><Link to="/privacy">Privacy Policy</Link></li>
            <li><Link to="/dpa">Data Processing Agreement</Link></li>
          </ul>
        </section>
      </div>

      <PublicFooter />
    </div>
  );
}

export default AcceptableUse;
